import { AuthProvider, useAuth } from '@data-delivery/okta-auth-react';
import {
  PermissionStateProvider,
  prefetchCurrentUserRoles,
} from '@data-delivery/permission-ui-data-access-state';
import { useConfig } from '@data-delivery/permission-ui-shared-environment';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React, { useEffect, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { ActivityTracker } from './activity-tracker';
import { oktaConfig } from './okta-config';

const InnerAuthBoundary: React.FC = () => {
  const { VITE_PERMISSION_API_URL } = useConfig();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      prefetchCurrentUserRoles().catch(() => {
        // Silently fail - the actual query will handle errors
      });
    }
  }, [isAuthenticated]);

  return (
    <PermissionStateProvider
      suppressToastStatuses={[401]}
      tokenIncludePatterns={[VITE_PERMISSION_API_URL]}
    >
      <Outlet />
      <ActivityTracker />
      <ReactQueryDevtools initialIsOpen={false} />
    </PermissionStateProvider>
  );
};

export const AuthWrapper: React.FC = () => {
  const defaultRedirectPath = useMemo(() => {
    try {
      const candidates = [
        sessionStorage.getItem('okta-original-uri'),
        sessionStorage.getItem('referrerPath'),
      ].filter(Boolean) as string[];
      const valid = candidates.find(v => v.startsWith('/'));
      return valid || '/';
    } catch {
      return '/';
    }
  }, []);

  return (
    <AuthProvider
      defaultRedirectPath={defaultRedirectPath}
      config={oktaConfig}
      enableDebugLogs={process.env.NODE_ENV === 'development'}
    >
      <InnerAuthBoundary />
    </AuthProvider>
  );
};
